<script>
import { productListHeaderProps, useProductListHeader } from "./mixin";
import {
  productListHeaderSortEmits,
  productListHeaderSortProps,
  useProductListHeaderSort
} from "./sort-mixin";
import ButtonIcon from "@/components/molecules/button-icon";
import ButtonBacktop from "@/components/molecules/button-backtop";
import LazyImage from "@/components/atoms/lazy-image";
import Badge from "@/components/atoms/badge";
import Breadcrumbs from "@/components/molecules/breadcrumbs/mobile";
import ChildrenCategories from "@/components/organisms/product-list/product-list-header/children-categories";
import Sort from "@/components/organisms/product-list/product-list-header/sort";
import Popin from "@/components/molecules/popin";
import Button from "@/components/atoms/button";
import Accordion from "@/components/molecules/accordion";
import AccordionItem from "@/components/molecules/accordion/accordion-item";
import Divider from "@/components/atoms/divider";
import HtmlParser from "@/components/organisms/cms-container/html-parser";

import pictoAlcoolWeb from "@/assets/images/picto-alcool-web.svg";

export default {
  name: "ProductListHeaderMobile",
  components: {
    LazyImage,
    ButtonIcon,
    ButtonBacktop,
    Badge,
    Sort,
    Breadcrumbs,
    ChildrenCategories,
    Popin,
    Button,
    Accordion,
    AccordionItem,
    Divider,
    HtmlParser
  },
  props: {
    scrolled: {
      type: Boolean,
      default: false
    },
    appliedFilters: {
      type: Array,
      required: false,
      default: () => []
    },
    pushDataExplications: {
      type: Object,
      required: false,
      default: null
    },
    ...productListHeaderProps(),
    ...productListHeaderSortProps()
  },
  emits: ["toggleFilter", ...productListHeaderSortEmits()],
  setup(props, context) {
    const scrolledData = ref(props.scrolled);
    const { mappedBreadcrumbs, showBreadcrumbs, backgroundStyle, cover } =
      useProductListHeader(props);

    const toggleFilter = () => {
      context.emit("toggleFilter");
    };

    const { sortChange } = useProductListHeaderSort(context);

    const handleScroll = (event) => {
      scrolledData.value = window.scrollY > 400;
    };

    const declinaison = computed(() => {
      if (props.isTheme) {
        return ["c-product-list-header-mobile--theme"];
      }
      return ["c-product-list-header-mobile--main"];
    });

    onMounted(() => {
      if (process.client) {
        window.addEventListener("scroll", handleScroll);
      }
    });

    onBeforeUnmount(() => {
      if (process.client) {
        window.removeEventListener("scroll", handleScroll);
      }
    });

    return {
      toggleFilter,
      sortChange,
      mappedBreadcrumbs,
      showBreadcrumbs,
      backgroundStyle,
      pictoAlcoolWeb,
      cover,
      scrolledData,
      declinaison
    };
  }
};
</script>

<template>
  <div class="c-product-list-header-mobile Mob_DP_Categories">
    <div
      v-if="showBreadcrumbs"
      class="c-product-list-header-mobile__nav-header"
    >
      <Breadcrumbs
        :breadcrumbs="mappedBreadcrumbs"
        :divider="false"
      />
    </div>
    <div
      v-if="isSearch && total === 0 && appliedFilters.length === 0"
      class="u-flex u-flex-justify-between u-mb-sm"
    >
      <p class="u-font-17 u-font-secondary u-font700 ">
        Désolé, il n’y a aucun résultat pour la recherche «{{ keyWord }}»
      </p>
    </div>

    <div
      :style="backgroundStyle"
      :class="declinaison"
      class="c-product-list-header-mobile__main-content"
    >
      <div
        :class="{
          'c-product-list-header-mobile__heading--search': isSearch && total !== null,
          'c-product-list-header-mobile__heading--alcool': alcool,
        }"
        class="c-product-list-header-mobile__heading"
      >
        <p
          v-if="isSearch && total !== null"
          class="c-product-list-header-mobile__label-search"
        >
          Résultats de recherche pour :
        </p>
        <h1
          :class="{'c-product-list-header-mobile__title--searched':total, 'c-product-list-hearder-mobile__title--theme' : isTheme }"
          class="c-product-list-header-mobile__title"
        >
          {{ title }}
          {{ !isSearch ? firstFilter : '' }}
          <span v-if="isSearch"> {{ keyWord }} </span>
          &nbsp;|&nbsp;<span class="c-product-list-header-mobile__total">{{ total }} {{ pluralize(total, 'produit') }}</span>
        </h1>
      </div>

      <ChildrenCategories
        v-if="childrenCategories && childrenCategories.length > 0"
        :children-categories="childrenCategories"
        :is-theme="isTheme"
      />
    </div>
    <div
      v-if="alcool"
      class="c-product-list-header-mobile__alcool"
    >
      <Button @click="$refs.alcoolInfo.show()">
        <LazyImage
          :src="pictoAlcoolWeb"
          width="84"
          height="38"
          class="u-flex-item-none"
        />
      </Button>
    </div>
    <div
      v-if="pushDataExplications && pushDataExplications.html_content"
      class="u-mt-md u-mr-sm40 u-ml-xs25"
    >
      <Accordion>
        <AccordionItem>
          <template #title>
            {{ pushDataExplications.titre }}
          </template>
          <div class="u-pt-xs25">
            <HtmlParser :content="pushDataExplications.html_content" />
          </div>
        </AccordionItem>
      </Accordion>
      <Divider
        dotted
        class="u-mt-xs25 u-mb-none"
      />
    </div>
    <div class="c-product-list-header-mobile__filters-wrapper Mob_DP_Filtres">
      <ButtonIcon
        v-if="(filters && filters.length>0) || (appliedFilters && appliedFilters.length>0)"
        icon-name="equalizer"
        radius="md"
        variant="toggle-filter"
        icon-color="black"
        icon-position="right"
        icon-size="md"
        class="c-product-list-header-mobile__filters-button"
        @click="toggleFilter()"
      >
        <span class="u-flex u-flex-align-center u-gap-xs">
          Filtrer
          <Badge
            v-if="appliedFilters.length > 0"
            tag="span"
            radius="full"
            variant="primary-light"
            size="sm"
            class="u-font-11 u-color-white u-px-xs25 u-py-xxs"
          >
            {{ appliedFilters.length }}
          </Badge>
        </span>
      </ButtonIcon>
      <div
        v-if="filters && filters.length>0"
        :class="scrolledData && 'u-block'"
        class="c-product-list-header-mobile__more-filters--scrolled"
      >
        <ButtonIcon
          radius="full"
          variant="primary"
          size="md"
          icon-name="arrow-up"
          icon-color="white"
          icon-position="right"
          icon-size="sm"
          @click="toggleFilter()"
        >
          <span class="u-font-400">Filtrer</span>
        </ButtonIcon>
      </div>
      <Sort
        v-if="total > 0"
        :sort-criteria="sortCriteria"
        @sortChange="sortChange"
      />
    </div>
    <ButtonBacktop />
    <Popin
      ref="alcoolInfo"
      slide-up
      size="md"
    >
      <p>
        La loi interdit la vente d’alcool aux mineurs
      </p>
    </Popin>
  </div>
</template>
<style lang="scss" scoped>
@import "@/assets/styles/components/product-list-header-mobile.scss";
</style>
